'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { composeSx } from '../../utils/styledUtils';

export interface DeltaPillProps {
  /** Numeric delta. Sign determines direction; magnitude is rendered with `formatValue`. */
  value: number;
  /**
   * Optional unit suffix (e.g. "pt", "%", "bps"). Rendered after the formatted magnitude.
   */
  unit?: string;
  /**
   * For metrics where down-is-good (e.g. utilization, error rate), pass `invert` to flip the
   * color mapping: negative → success, positive → error.
   */
  invert?: boolean;
  /**
   * Custom formatter for the magnitude. Receives the absolute value. Default uses one decimal
   * place when |value| < 10, else integer.
   */
  formatValue?: (absValue: number) => string;
  /** Hide the leading ±. Default false (the sign is shown). */
  hideSign?: boolean;
  /** Sx overrides. */
  sx?: SxProps<Theme>;
  /** Aria label override. */
  ariaLabel?: string;
}

function defaultFormat(absValue: number): string {
  if (!Number.isFinite(absValue)) return '—';
  if (absValue === 0) return '0';
  if (absValue < 10) return absValue.toFixed(1);
  return Math.round(absValue).toString();
}

export function DeltaPill({
  value,
  unit,
  invert = false,
  formatValue = defaultFormat,
  hideSign = false,
  sx,
  ariaLabel,
}: DeltaPillProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isFinite = Number.isFinite(value);
  const isPositive = isFinite && value > 0;
  const isZero = isFinite && value === 0;

  let severity: 'success' | 'error' | 'neutral';
  if (!isFinite || isZero) severity = 'neutral';
  else if (isPositive) severity = invert ? 'error' : 'success';
  else severity = invert ? 'success' : 'error';

  let bg: string;
  let fg: string;
  if (severity === 'neutral') {
    bg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    fg = theme.palette.text.secondary;
  } else {
    const palette = theme.palette[severity];
    bg = alpha(palette.main, isDark ? 0.16 : 0.1);
    fg = isDark ? palette.light : palette.dark;
  }

  const sign = !isFinite || hideSign || isZero ? '' : isPositive ? '+' : '−';
  const magnitude = formatValue(Math.abs(value));
  const text = `${sign}${magnitude}${unit ?? ''}`;
  const composedAria =
    ariaLabel ??
    (!isFinite
      ? 'No delta available'
      : isZero
        ? `No change ${unit ?? ''}`
        : `${isPositive ? 'Increase' : 'Decrease'} of ${magnitude}${unit ? ` ${unit}` : ''}`);

  return (
    <Box
      role="status"
      aria-label={composedAria}
      sx={composeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          height: 18,
          px: 0.75,
          borderRadius: '4px',
          backgroundColor: bg,
          color: fg,
        },
        sx,
      )}
    >
      <Typography
        component="span"
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.01em',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

DeltaPill.displayName = 'DeltaPill';

export default DeltaPill;
